import Footer from "./Footer";
import Header from "./header";
import { Helmet } from "react-helmet-async";

const styles = {
  pageWrapper: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    width: "100%",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: "#FFFFFF",
    overflowX: "hidden",
  },
  container: {
    width: "80%",
    maxWidth: "1600px",
    margin: "0 auto",
    padding: "40px 0",
    boxSizing: "border-box",
    flex: "1",
    marginTop: "50px",
  },
};

const RefundPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Refund Policy | CGRMART</title>

        <meta
          name="description"
          content="Read CGRMART return and refund policy for EV chargers, including eligibility, process, timelines, and conditions."
        />

        <meta
          name="keywords"
          content="refund policy, return policy, EV charger refund, CGRMART returns, Tucker Motors refund"
        />

        <meta name="robots" content="index, follow" />

        <meta property="og:title" content="Refund Policy | CGRMART" />
        <meta
          property="og:description"
          content="Understand CGRMART return, refund, and replacement policy for EV charging products."
        />
        <meta property="og:type" content="website" />

        <link rel="canonical" href="https://cgrmart.com/refund-policy" />
      </Helmet>

      <Header />

      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div className="max-w-4xl mx-auto p-6 text-gray-800">

            <h1 className="text-3xl font-bold mb-2">
              CGRMART – Product Return & Refund Policy
            </h1>

            <p className="mb-6 text-sm text-gray-500">
              Effective Date: 21 March 2026
            </p>

            {/* 1 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">1. Purpose</h2>
            <p className="mb-4">
              This policy defines the terms under which Tucker Motors Private Limited accepts returns,
              processes refunds, and handles replacement requests for EV chargers and related products.
            </p>

            {/* 2 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">2. Applicability</h2>
            <ul className="list-disc pl-6 mb-4">
              <li>Tucker – Manufacturer of EV charging products</li>
              <li>Charge Point Operators (CPOs)</li>
              <li>Customers purchasing directly or via authorised dealers</li>
            </ul>

            {/* 3 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              3. Eligible Products for Return
            </h2>
            <ul className="list-disc pl-6 mb-4">
              <li>Purchased from Tucker or authorised dealer</li>
              <li>Returned within 7 days of delivery</li>
              <li>Unused and in original packaging</li>
              <li>Includes invoice, manuals, and accessories</li>
            </ul>

            <p className="mb-2 font-medium">Valid reasons:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Manufacturing defect</li>
              <li>Wrong product delivered</li>
              <li>Transit damage (reported within 24 hours)</li>
            </ul>

            {/* 4 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              4. Non-Returnable Products
            </h2>
            <ul className="list-disc pl-6 mb-4">
              <li>Installed or used products</li>
              <li>Improper handling or damaged items</li>
              <li>Custom-built or special-order products</li>
              <li>Software or digital products</li>
            </ul>

            {/* 5 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              5. Return Process
            </h2>

            <h3 className="font-semibold mt-4 mb-1">Step 1</h3>
            <p className="mb-2">
              Contact support at{" "}
              <span className="text-red-600 font-semibold">
                support@tuckermotors.com
              </span>
            </p>

            <h3 className="font-semibold mt-4 mb-1">Step 2</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Invoice copy</li>
              <li>Serial number</li>
              <li>Product photos</li>
              <li>Reason for return</li>
            </ul>

            <h3 className="font-semibold mt-4 mb-1">Step 3</h3>
            <p className="mb-2">Verification within 3 working days</p>

            <h3 className="font-semibold mt-4 mb-1">Step 4</h3>
            <p className="mb-2">Return pickup or drop-off instructions</p>

            <h3 className="font-semibold mt-4 mb-1">Step 5</h3>
            <p className="mb-4">Inspection before approval</p>

            {/* 6 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              6. Refund Terms
            </h2>
            <ul className="list-disc pl-6 mb-4">
              <li>Processed within 7–10 working days after approval</li>
              <li>Refund to original payment method</li>
              <li>Dealer purchases handled by dealer</li>
              <li>Shipping charges non-refundable (except company error)</li>
            </ul>

            {/* 7 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              7. Replacement Policy
            </h2>
            <ul className="list-disc pl-6 mb-4">
              <li>Replacement available for manufacturing defects</li>
              <li>Warranty continues from original purchase date</li>
            </ul>

            {/* 8 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              8. Responsibilities
            </h2>

            <p className="font-semibold">Tucker</p>
            <ul className="list-disc pl-6 mb-3">
              <li>Ensure product quality</li>
              <li>Process refund/replacement</li>
            </ul>

            <p className="font-semibold">Customer / CPO</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Inspect products upon delivery</li>
              <li>Maintain packaging</li>
              <li>Handle products carefully</li>
            </ul>

            {/* 9 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              9. Legal Compliance & Jurisdiction
            </h2>
            <p className="mb-4">
              This policy is governed by Indian laws. All disputes fall under the
              jurisdiction of courts in Madurai, Tamil Nadu.
            </p>

            {/* 10 */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              10. Amendments
            </h2>
            <p className="mb-4">
              Tucker reserves the right to modify this policy at any time.
              Updates will be published on the official website and app.
            </p>

          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default RefundPolicy;